/**
 * External dependencies
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Internal dependencies
 */
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import ImageFilePicker from '@Components/ImageFilePicker';
import { AspectRatio } from '@Components/AspectRatio';
import { Skeleton } from '@Components/Skeleton';
import { CheckIcon, FileTextIcon } from 'lucide-react';
import { useAssets } from '../../hooks/useAssets';
import { useUploadImagesToOrgMutation } from '../../hooks/useCloudinaryUpload';
import { cn } from '../../utils/cn';

export interface PacketDialogProps {
  /** Display name shown in the dialog title (e.g. venue name) */
  name: string;
  /** Human-readable packet label, e.g. "Private Dining" or "Corporate" */
  packetLabel: string;
  /**
   * Tag applied to uploaded packets and used to filter the "choose existing"
   * library so it only shows PDFs of this packet type.
   */
  packetTag: string;
  /** The current packet asset ID, if any */
  currentPacketAssetId: string | null | undefined;
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Called when the dialog should open or close */
  onOpenChange: (open: boolean) => void;
  /**
   * Called with the selected asset ID when the user confirms.
   * The caller is responsible for persisting this (e.g. updating the venue).
   * Should return a promise that resolves when the save is complete.
   */
  onSave: (assetId: string) => Promise<void>;
}

const PacketFormSchema = z.object({
  packetAssetId: z.string().optional().nullable(),
});

type PacketFormValues = z.infer<typeof PacketFormSchema>;

export default function PacketDialog({
  name,
  packetLabel,
  packetTag,
  currentPacketAssetId,
  isOpen,
  onOpenChange,
  onSave,
}: PacketDialogProps) {
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: assets,
    status,
    refetch,
  } = useAssets({
    enabled: isOpen,
  });

  // Only show PDFs that have been tagged for this packet type.
  const packetAssets = (assets ?? []).filter((asset) =>
    asset.tags?.some(
      (t) => t.tag.name.toLowerCase() === packetTag.toLowerCase(),
    ),
  );

  const packetForm = useForm<PacketFormValues>({
    resolver: zodResolver(PacketFormSchema),
    defaultValues: {
      packetAssetId: currentPacketAssetId ?? null,
    },
  });

  const { mutateAsync: uploadImages, isPending: isUploading } =
    useUploadImagesToOrgMutation();

  const isBusy = isUploading || isSaving;

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    setPendingFile(null);
    packetForm.reset({
      packetAssetId: currentPacketAssetId ?? null,
    });
    packetForm.clearErrors('root');
  };

  const handleFileChange = (file: File | null) => {
    setPendingFile(file);
    if (file) {
      packetForm.setValue('packetAssetId', null, {
        shouldDirty: true,
      });
    }
  };

  const onSubmit = async (values: PacketFormValues) => {
    packetForm.clearErrors('root');

    let assetIdToUse = values.packetAssetId || null;

    if (pendingFile) {
      try {
        const result = await uploadImages({
          orgId: '',
          files: [pendingFile],
          // Tag the upload so it shows up in this packet type's library.
          assetTags: [{ name: packetTag }],
        });

        const cloudinaryItems = Array.isArray(result) ? result : [result];
        const publicId = (cloudinaryItems[0] as any)?.public_id;

        if (!publicId) {
          packetForm.setError('root', {
            type: 'server',
            message:
              'Upload succeeded but could not identify the file. Please try again.',
          });
          return;
        }

        const { data: freshAssets } = await refetch();

        const matchedAsset = (freshAssets as any[])?.find(
          (a: any) => a.cloudinaryId === publicId,
        );

        if (!matchedAsset?.id) {
          packetForm.setError('root', {
            type: 'server',
            message:
              'Upload succeeded but the asset could not be found. Please try again.',
          });
          return;
        }

        assetIdToUse = matchedAsset.id;
      } catch (error: any) {
        console.error('Error uploading packet:', error);
        packetForm.setError('root', {
          type: 'server',
          message: error?.message || 'Failed to upload file. Please try again.',
        });
        return;
      }
    }

    if (!assetIdToUse) return;

    try {
      setIsSaving(true);
      await onSave(assetIdToUse);
      setPendingFile(null);
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error saving packet:', error);
      packetForm.setError('root', {
        type: 'server',
        message: `We were unable to update the ${packetLabel} packet. Please try again.`,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const selectedAssetId = packetForm.watch('packetAssetId');
  const hasSelection = !!pendingFile || !!selectedAssetId;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            Select {packetLabel} packet for {name}
          </DialogTitle>
          <p className="text-sm text-zinc-500">
            Upload a PDF or choose a previously uploaded {packetLabel} packet.
          </p>
        </DialogHeader>

        <Form {...packetForm}>
          <form
            onSubmit={packetForm.handleSubmit(onSubmit)}
            className="space-y-6"
          >
            {packetForm.formState.errors.root?.message && (
              <div className="mb-4">
                <Alert variant="destructive">
                  <AlertTitle>Something went wrong</AlertTitle>
                  <AlertDescription>
                    {packetForm.formState.errors.root?.message}
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {/* Upload new packet */}
            <div>
              <div className="mb-2 text-sm font-medium text-zinc-700">
                Upload new PDF:
              </div>
              <ImageFilePicker
                inputId={`${packetTag.toLowerCase().replace(/\s+/g, '-')}-packet-upload-input`}
                accept="pdf"
                showTags={false}
                file={pendingFile}
                onFileChange={handleFileChange}
              />
            </div>

            {/* OR divider */}
            <div className="flex items-center gap-4">
              <div className="h-px flex-1 bg-zinc-200" />
              <span className="text-sm text-zinc-400">OR</span>
              <div className="h-px flex-1 bg-zinc-200" />
            </div>

            {/* Choose from previously uploaded packets */}
            <div>
              <div className="mb-2 text-sm font-medium text-zinc-700">
                Choose an existing {packetLabel} packet:
              </div>
              <FormField
                control={packetForm.control}
                name="packetAssetId"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormControl>
                        <div className="grid max-h-128 grid-cols-3 gap-3 overflow-y-auto p-1">
                          {status === 'pending' ? (
                            <>
                              <Skeleton className="aspect-square w-full rounded-md" />
                              <Skeleton className="aspect-square w-full rounded-md" />
                              <Skeleton className="aspect-square w-full rounded-md" />
                            </>
                          ) : packetAssets.length === 0 ? (
                            <div className="col-span-3 flex h-28 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-100 text-center text-sm text-zinc-500">
                              <div className="font-medium text-zinc-700">
                                No uploaded {packetLabel} packets
                              </div>
                            </div>
                          ) : (
                            packetAssets.map((asset) => {
                              const selected =
                                !pendingFile && field.value === asset.id;
                              const tagNames =
                                asset.tags?.map((t) => t.tag.name) ?? [];
                              return (
                                <button
                                  key={asset.id}
                                  type="button"
                                  onClick={() => {
                                    setPendingFile(null);
                                    field.onChange(
                                      field.value === asset.id
                                        ? null
                                        : asset.id,
                                    );
                                  }}
                                  className={cn(
                                    'group relative flex w-full flex-col overflow-hidden rounded-md border bg-zinc-200 transition',
                                    selected
                                      ? 'border-primary ring-4 ring-[#CAAB6A]'
                                      : 'border-transparent hover:border-zinc-300',
                                  )}
                                >
                                  <AspectRatio
                                    ratio={1}
                                    className="relative w-full"
                                  >
                                    <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-zinc-100 text-zinc-500">
                                      <FileTextIcon className="h-10 w-10" />
                                      <span className="px-2 text-center text-[10px] break-all">
                                        {asset.cloudinaryId}
                                      </span>
                                    </div>
                                    {selected && (
                                      <div className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full border-2 border-[#CAAB6A] bg-black">
                                        <CheckIcon className="h-4 w-4 text-[#CAAB6A]" />
                                      </div>
                                    )}
                                  </AspectRatio>
                                  {tagNames.length > 0 && (
                                    <div className="flex flex-wrap gap-1 bg-white px-1.5 py-1.5">
                                      {tagNames.map((tagName) => (
                                        <span
                                          key={tagName}
                                          className="bg-secondary text-secondary-foreground rounded-md px-1.5 py-0.5 text-[10px] font-medium"
                                        >
                                          {tagName}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </button>
                              );
                            })
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="link" disabled={isBusy}>
                  Cancel
                </Button>
              </DialogClose>

              <Button
                type="submit"
                variant="primary"
                loading={isBusy}
                disabled={isBusy || !hasSelection}
              >
                {pendingFile ? 'Upload and use PDF' : 'Use PDF'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
